import { useQuery } from "@apollo/client";
import { DOMAIN, useHasRoles } from "@/hooks/useCapabilities";
import { NavLink } from "@/layouts/AppLayout/types";
import { Path } from "@/router";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import { FACILITY_DETAILS_HEADER_QUERY, FacilityDetailsHeaderQueryResponse } from "./queries";

export const getFacilityNavLinks: (
  facilityId: string,
  options?: { showAdminTools?: boolean }
) => Record<string, NavLink<Path>[]> = (facilityId, options) => {
  const facilityLinks = [
    {
      domain: DOMAIN.FACILITIES,
      label: "Details",
      link: {
        to: "/facilities/:facilityId/details",
        params: { facilityId }
      },
      icon: "assignment_ind"
    },
    {
      domain: DOMAIN.FACILITIES,
      label: "People",
      link: {
        to: "/facilities/:facilityId/people",
        params: { facilityId }
      },
      icon: "group"
    },
    {
      domain: DOMAIN.FACILITIES,
      label: "Jobs",
      link: {
        to: "/facilities/:facilityId/jobs",
        params: { facilityId }
      },
      icon: "business_center"
    },
    options?.showAdminTools && {
      domain: DOMAIN.FACILITIES,
      label: "Admin Tools",
      link: {
        to: "/facilities/:facilityId/admin-tools",
        params: { facilityId }
      },
      icon: "build"
    }
  ].filter(Boolean) as NavLink<Path>[];

  return {
    "Manage Facility": facilityLinks
  };
};

export function useFacilityAdminToolsAccess(facilityId?: string) {
  const isAdmin = useHasRoles([ROLE.GLOBAL_ADMIN]);
  const { data, loading } = useQuery<FacilityDetailsHeaderQueryResponse>(FACILITY_DETAILS_HEADER_QUERY, {
    variables: { facilityId: facilityId ?? "" },
    skip: !isAdmin || !facilityId
  });

  const facility = data?.facility;
  const legacyDigits = facility?.legacyId?.replace(/\D/g, "") ?? "";
  const hasFacilityLegacyIdForSync = legacyDigits.length >= 1 && legacyDigits.length <= 10;

  return {
    canAccessAdminTools: !!facilityId && isAdmin && hasFacilityLegacyIdForSync,
    facility,
    legacyDigits,
    loading: !!facilityId && isAdmin && loading
  };
}

export function useFacilityNavLinks(facilityId?: string) {
  const { canAccessAdminTools } = useFacilityAdminToolsAccess(facilityId);
  if (!facilityId) return { "Manage Facility": [] };

  return getFacilityNavLinks(facilityId, { showAdminTools: canAccessAdminTools });
}
